import React from 'react';

import type {
  NavigationSource,
  DialogBackEvent,
} from './startDialogBrowserBack';

import {
  startDialogBrowserBack,
  afterDialogEntryLeft,
  DIALOG_BACK_EVENT,
  OVERLAY_MARKER,
} from './startDialogBrowserBack';

export const DialogBrowserBack = ({
  router,
}: {
  router?: NavigationSource;
}) => {
  React.useEffect(() => startDialogBrowserBack(router), [router]);
  return null;
};

/**
 * `afterDialogEntryLeft` for a write to this page's url: a dropdown's choice, a
 * dialog's close. Left for another page meanwhile, the write would land on that
 * page's url, so it is dropped.
 */
export function useAfterDialogEntryLeft() {
  const isMounted = React.useRef(true);
  React.useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  return React.useCallback((work: () => void) => {
    afterDialogEntryLeft(() => {
      if (isMounted.current) work();
    });
  }, []);
}

/**
 * For a dialog with screens of its own (a preview's drill-down): while
 * `enabled`, browser back steps one screen back via `onStep` instead of
 * closing the dialog. Attach the returned ref to any element inside the
 * dialog — it is how the back is matched to THIS dialog when several are open.
 */
export function useDialogBackStep<T extends Element>(
  enabled: boolean,
  onStep: () => void,
) {
  const ref = React.useRef<T>(null);
  const onStepRef = React.useRef(onStep);
  onStepRef.current = onStep;

  React.useEffect(() => {
    if (!enabled) return;
    const handleBack = (event: Event) => {
      const { dialog } = (event as DialogBackEvent).detail;
      if (!ref.current || !dialog.contains(ref.current)) return;
      event.preventDefault();
      onStepRef.current();
    };
    window.addEventListener(DIALOG_BACK_EVENT, handleBack);
    return () => window.removeEventListener(DIALOG_BACK_EVENT, handleBack);
  }, [enabled]);

  return ref;
}

/**
 * For an overlay of our own that is neither our `Modal` nor built on MUI's
 * (the phone nav drawer, a floating menu): spread the returned props on its
 * outermost element, rendered only while it is open, and browser back closes
 * it via `onClose` like any dialog.
 */
export function useOverlayBackClose<T extends Element>(
  open: boolean,
  onClose: () => void,
) {
  const ref = useDialogBackStep<T>(open, onClose);
  return { [OVERLAY_MARKER]: '', ref };
}
